'use client'

import { useSetAtom, useStore } from 'jotai'
import { useCallback, useContext } from 'react'

import { LinkingPageContext } from '@/features/dashboard/lib/linking-page-context'
import { resolveOrderFormTarget } from '@/lib/resolve-order-form-target'
import {
  DEFAULT_ORDER_FORM_CARD_ID,
  orderFormPopupAnchorAtom,
  orderFormPopupCardIdAtom,
  orderFormPopupFormAtom,
  registeredOrderContainersAtom,
} from '@/state/order'

export type PopupAnchor = {
  right: number
  left: number
  top: number
  bottom?: number
} | null

/**
 * Opens the order form popup, but suppresses it if the resolved OrderContainer
 * is already mounted. External row injection must not override a mode the user
 * explicitly selected (especially Chat).
 *
 * Target resolution uses `resolveOrderFormTarget` (lastFocused → fallback), matching
 * the same logic used by `useOrderFormDispatch` so popup and dispatch always agree.
 */
export function useOrderFormPopup() {
  const store = useStore()
  const pageId = useContext(LinkingPageContext)
  const setAnchor = useSetAtom(orderFormPopupAnchorAtom)
  const setPopupCardId = useSetAtom(orderFormPopupCardIdAtom)
  const setPopupForm = useSetAtom(orderFormPopupFormAtom)

  const openOrderFormPopup = useCallback(
    (triggerCardId: string, anchor: PopupAnchor, options?: { force?: boolean }): boolean => {
      // `force` opens the sheet even over a docked Order widget. It still shows that
      // widget's shared form (the sheet reads the resolved target), but is keyed by the
      // default id so the widget and the sheet never both own the submit shortcut.
      if (options?.force) {
        setAnchor(anchor)
        const target = resolveOrderFormTarget(store, triggerCardId, pageId)
        setPopupForm(target === DEFAULT_ORDER_FORM_CARD_ID ? null : target)
        setPopupCardId(DEFAULT_ORDER_FORM_CARD_ID)
        return true
      }
      const orderContainerId = resolveOrderFormTarget(store, triggerCardId, pageId)
      const registeredContainers = store.get(registeredOrderContainersAtom)
      if (registeredContainers.has(orderContainerId)) return false
      setAnchor(anchor)
      setPopupForm(null)
      // orderContainerId is the popup's own ticket here — DEFAULT_ORDER_FORM_CARD_ID, or the
      // trigger's link group's parked ticket when that group has no Order Form (any mounted
      // form was ruled out above). Using it directly, rather than `triggerCardId`, is what
      // makes the popup opened by one widget (e.g. the chart's BUY/SELL) the SAME session as
      // one opened moments later by another widget in that group (e.g. the watchlist). See
      // computeOrderFormTarget.
      setPopupCardId(orderContainerId)
      return true
    },
    [store, pageId, setAnchor, setPopupCardId, setPopupForm]
  )

  return { openOrderFormPopup }
}
